import {useMemo} from 'react';
import {QueryKey, useQuery, UseQueryResult} from 'react-query';

import {BoxType, Features, QuestionTypes, SurveySlug, WidgetTypes} from 'client/common/types';

import {useFeatures} from 'client/common/contexts/features';

import {useBoxType} from 'client/common/hooks/box';

import {SurveyPreset} from 'client/common/types/survey';
import {getSurvey, getSurveyFeatures} from 'client/common/utils/api-client';

import {
    CREATE_SEGMENT_SURVEY_FEATURES,
    PRO_SURVEY_FEATURES,
    SIMPLE_SURVEY_FEATURES,
    FEATURES_BY_PURPOSE,
    UX_SURVEY_FEATURES
} from './consts';

export function getSurveyFeaturesKey(slug: SurveySlug): QueryKey {
    return ['survey-features', slug];
}

function getSurveyFeaturesByBoxType(boxType: BoxType | undefined) {
    if (boxType === 'createSegment') {
        return CREATE_SEGMENT_SURVEY_FEATURES;
    }

    if (boxType === 'videoBox' || boxType === 'videoBoxKZ') {
        return SIMPLE_SURVEY_FEATURES;
    }

    if (boxType === 'ux') {
        return UX_SURVEY_FEATURES;
    }

    return;
}

function getSurveyFeaturesByPurpose(purposeName: SurveyPreset) {
    return FEATURES_BY_PURPOSE[purposeName] || null;
}

export function useSurveyFeatures(slug: SurveySlug): UseQueryResult<Features> {
    const boxType = useBoxType(slug);

    return useQuery(getSurveyFeaturesKey(slug), async (): Promise<Features> => {
        const {data} = await getSurveyFeatures({slug});
        const {data: survey} = await getSurvey({slug});

        const purpose = survey?.purpose;

        const features = {
            ownAudience: Boolean(data.ownAudience),
            fiveSecTest: Boolean(data.fiveSecTest),
            manualAudience: Boolean(data.manualAudience)
        };

        const featuresByPurpose = purpose?.selected
            ? getSurveyFeaturesByPurpose(purpose.selected)
            : null;

        if (featuresByPurpose) {
            return {...featuresByPurpose, ...features};
        }

        const featuresByBoxType = getSurveyFeaturesByBoxType(boxType) || PRO_SURVEY_FEATURES;

        return {...featuresByBoxType, ...features};
    });
}

export function useAvailablePageChildTypes() {
    const features = useFeatures();

    return useMemo(() => {
        return [...QuestionTypes, ...WidgetTypes].filter(type => features[type]?.enabled);
    }, [features]);
}

export function useIsDualChoiceAvailable() {
    const features = useFeatures();

    return features.choice?.canUseDualChoice;
}
